import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax
import 'mapbox-gl/dist/mapbox-gl.css';
import moment from 'moment';

import './Map.css';
import {
  UPDATED_RECENTLY_SECS,
  UPDATED_VERY_RECENTLY_SECS,
} from '../consts';

const getServiceId = service => `${service.service_id}-${service.vehicle}`;

const getPopupHtml = (service) => {
  const updated = moment(service.last_updated);
  const diff = moment().diff(updated, 'seconds');

  let colour = '#ff0000';
  if (diff < UPDATED_RECENTLY_SECS) {
    colour = '#ffbe00';
  }
  if (diff < UPDATED_VERY_RECENTLY_SECS) {
    colour = '#26aa5a';
  }

  let markup = '';
  if (service.name) {
    markup += `<h4 style="margin: 0;">${service.name}</h4>`;
  }

  if (service.vehicle) {
    markup += `<h4 style="margin: 0;">${service.vehicle}</h4>`;
  }

  markup += `<p style="margin: 0;">
  Last seen: <span style="color: ${colour}">${updated.format('HH:mm:ss')}</span>
  </p>`;

  return markup;
};

const Mapbox = ({
  apiKey,
  lessRecentUpdates,
  services
}) => {
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);

  const [markers, setMarkers] = useState([]);
  const [firstLoad, setFirstLoad] = useState(true);

  const doOnMapLoad = (fn) => {
    if (!mapRef.current.loaded()) {
      mapRef.current.once('load', fn);
      return;
    }

    fn();
  };

  useEffect(() => {
    const first = services[0];

    if (mapRef.current === null) {
      mapboxgl.accessToken = apiKey;
      mapRef.current = new mapboxgl.Map({
        container: mapContainerRef.current,
        center: [first.long, first.lat],
        style: 'mapbox://styles/mapbox/streets-v11',
        zoom: 13
      });
    }

    return () => {
      mapRef.current.remove();
    }
  }, []);

  useEffect(() => {
    doOnMapLoad(() => {
      const newMarkers = services.filter(
        service => !markers.find(marker => marker.id === getServiceId(service))
      ).map(
        service => {
          const key = getServiceId(service);
          return {
            id: key,
            marker: new mapboxgl.Marker({ color: 'black' })
              .setPopup(new mapboxgl.Popup({ offset: 25 }).setHTML(getPopupHtml(service)))
              .setLngLat([service.long, service.lat])
              .addTo(mapRef.current)
          };
        }
      );

      const existingMarkers = markers.filter(
        ({ id, marker }) => {
          const service = services.find(service => getServiceId(service) === id);

          if (!service) {
            marker.remove();
            return false;
          }

          const { long, lat } = service;
          marker.setLngLat([ long, lat ]);
          marker.removeClassName('live-marker');
          marker.addClassName('live-marker');
          marker.setPopup(new mapboxgl.Popup({ offset: 25 }).setHTML(getPopupHtml(service)));

          return true;
        }
      );

      setMarkers([
        ...newMarkers,
        ...existingMarkers
      ]);
    });
  }, [services]);

  useEffect(() => {
    if (!markers.length) {
      return;
    }

    if (firstLoad) {
      const bounds = new mapboxgl.LngLatBounds();
      markers.forEach(({ marker }) => bounds.extend(marker.getLngLat()));
      if (markers.length > 1) {
        doOnMapLoad(() => mapRef.current.fitBounds(bounds, { padding: 50 }));
      }
      setFirstLoad(false);
    }

    if (markers.length === 1) {
      doOnMapLoad(() => mapRef.current.setCenter(markers[0].marker.getLngLat()));
    }
  }, [markers])

  return (
    <>
      <div style={{ paddingTop: 0 }}>
        {lessRecentUpdates > 0 && (
          <div style={{
            position: 'absolute',
            zIndex: 999,
            background: '#ffeb00',
            padding: 5,
            fontSize: 12
          }}>
            {markers.length === lessRecentUpdates && (
              <p style={{ margin: 0 }}>
                Location updates have recently stopped. This may mean the {lessRecentUpdates > 1 ? `vehicles are` : `vehicle is`} stationary at a bus stop, in traffic, affected by poor signal or the journey has now ended.
              </p>
            ) || (
              <p style={{ margin: 0 }}>
                Location updates for {lessRecentUpdates} vehicles have recently stopped. This may mean vehicles are stationary at a bus stop, in traffic, affected by poor signal or their journey has now ended.
              </p>
            )}
          </div>
        )}
      </div>

      <div id="root">
        <div id="map" ref={mapContainerRef} />
      </div>
    </>
  );
};

export default Mapbox;
